import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Car, Inbox, CheckCircle2, Mail, Phone, RefreshCw, Loader2, TrendingUp, Eye, Share2, FileText } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, fileUrl, euro, km } from "../lib/api";
import { CarForm } from "../components/CarForm";
import { ContentEditor } from "../components/ContentEditor";

export default function Admin() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("occasions");
  const [cars, setCars] = useState([]);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null); // null=closed, {}=new, {..}=edit
  const [syncing, setSyncing] = useState(false);

  useEffect(() => { if (ready && !user) nav("/login"); }, [ready, user, nav]);

  const load = useCallback(() => {
    api.get("/cars", { params: { show_all: true } }).then((r) => setCars(r.data)).catch(() => {});
    api.get("/requests").then((r) => setRequests(r.data)).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  const del = async (id) => {
    if (!window.confirm("Weet u zeker dat u deze auto wilt verwijderen?")) return;
    await api.delete(`/cars/${id}`);
    toast.success("Auto verwijderd");
    load();
  };

  const markRead = async (id) => { await api.patch(`/requests/${id}/read`); load(); };
  const delReq = async (id) => { await api.delete(`/requests/${id}`); toast.success("Aanvraag verwijderd"); load(); };

  const syncMobilox = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/sync-mobilox");
      if (data.error) toast.error(`Sync mislukt: ${data.error}`);
      else if (data.imported > 0) { toast.success(`${data.imported} voertuigen geïmporteerd uit Mobilox`); load(); }
      else toast.info(data.note || "Geen voertuigen gevonden — Mobilox-voorraad is mogelijk nog leeg.");
    } catch (e) {
      toast.error("Sync mislukt. Probeer het later opnieuw.");
    } finally {
      setSyncing(false);
    }
  };

  if (!ready || !user) return <div className="min-h-screen grid place-items-center text-text-muted">Laden...</div>;

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="border-b border-border bg-surface sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/" className="font-display font-black text-lg">STOOF <span className="text-primary text-xs align-top">beheer</span></Link>
            <nav className="hidden sm:flex gap-1">
              <TabBtn active={tab === "occasions"} onClick={() => setTab("occasions")} icon={Car} label="Occasions" />
              <TabBtn active={tab === "aanvragen"} onClick={() => setTab("aanvragen")} icon={Inbox} label={`Aanvragen${stats?.ongelezen ? ` (${stats.ongelezen})` : ""}`} />
              <TabBtn active={tab === "populair"} onClick={() => setTab("populair")} icon={TrendingUp} label="Populair" />
              <TabBtn active={tab === "teksten"} onClick={() => setTab("teksten")} icon={FileText} label="Teksten" />
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden md:block text-sm text-text-muted" data-testid="admin-user-email">{user?.email}</span>
            <button onClick={() => { logout(); nav("/login"); }} data-testid="logout-btn" className="inline-flex items-center gap-2 text-sm font-semibold text-text-muted hover:text-primary transition-colors">
              <LogOut size={16} /> Uitloggen
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border mb-10">
            <Stat label="Occasions" value={stats.totaal_autos} />
            <Stat label="Verkocht" value={stats.verkocht} />
            <Stat label="Aanvragen" value={stats.aanvragen} />
            <Stat label="Ongelezen" value={stats.ongelezen} accent />
          </div>
        )}

        {/* mobile tabs */}
        <div className="sm:hidden flex gap-2 mb-6">
          <TabBtn active={tab === "occasions"} onClick={() => setTab("occasions")} icon={Car} label="Occasions" />
          <TabBtn active={tab === "aanvragen"} onClick={() => setTab("aanvragen")} icon={Inbox} label="Aanvragen" />
          <TabBtn active={tab === "populair"} onClick={() => setTab("populair")} icon={TrendingUp} label="Populair" />
          <TabBtn active={tab === "teksten"} onClick={() => setTab("teksten")} icon={FileText} label="Teksten" />
        </div>

        {tab === "occasions" && (
          <div>
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <h2 className="font-display font-bold text-2xl">Occasions beheren</h2>
              <div className="flex items-center gap-3">
                <button onClick={syncMobilox} disabled={syncing} data-testid="sync-mobilox-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-primary/30 text-primary font-semibold px-5 py-2.5 hover:bg-primary/5 transition-colors disabled:opacity-60">
                  {syncing ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
                  {syncing ? "Synchroniseren..." : "Sync met Mobilox"}
                </button>
                <button onClick={() => setEditing({})} data-testid="add-car-btn" className="inline-flex items-center gap-2 rounded-full bg-primary text-white font-semibold px-5 py-2.5 hover:bg-primary-light transition-colors">
                  <Plus size={18} /> Auto toevoegen
                </button>
              </div>
            </div>
            <div className="border border-border bg-surface overflow-x-auto">
              <table className="w-full text-sm" data-testid="cars-table">
                <thead>
                  <tr className="border-b border-border text-left text-text-muted uppercase text-xs tracking-wide">
                    <th className="p-4 font-semibold">Auto</th>
                    <th className="p-4 font-semibold hidden md:table-cell">Bouwjaar</th>
                    <th className="p-4 font-semibold hidden md:table-cell">KM</th>
                    <th className="p-4 font-semibold">Prijs</th>
                    <th className="p-4 font-semibold">Status</th>
                    <th className="p-4 font-semibold text-right">Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {cars.map((c) => (
                    <tr key={c.id} className="border-b border-border last:border-0 hover:bg-background/60" data-testid={`car-row-${c.id}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-14 h-11 bg-[#eef2f0] overflow-hidden shrink-0">
                            {c.images?.[0] && <img src={fileUrl(c.images[0])} alt="" className="w-full h-full object-cover" />}
                          </div>
                          <div>
                            <p className="font-semibold text-text-main">{c.merk} {c.model}</p>
                            <p className="text-xs text-text-muted">{c.brandstof} · {c.transmissie}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 hidden md:table-cell text-text-muted">{c.bouwjaar}</td>
                      <td className="p-4 hidden md:table-cell text-text-muted">{km(c.kilometerstand)}</td>
                      <td className="p-4 font-semibold">{euro(c.prijs)}</td>
                      <td className="p-4">
                        <div className="flex flex-col gap-1">
                          {c.verkocht && <span className="text-xs bg-text-main text-white px-2 py-0.5 w-fit">Verkocht</span>}
                          {c.uitgelicht && <span className="text-xs bg-accent text-primary px-2 py-0.5 w-fit font-semibold">Uitgelicht</span>}
                          {!c.verkocht && !c.uitgelicht && <span className="text-xs text-text-muted">Te koop</span>}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          <button onClick={() => setEditing(c)} data-testid={`edit-car-${c.id}`} className="p-2 hover:text-primary transition-colors"><Pencil size={16} /></button>
                          <button onClick={() => del(c.id)} data-testid={`delete-car-${c.id}`} className="p-2 hover:text-red-600 transition-colors"><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "aanvragen" && (
          <div>
            <h2 className="font-display font-bold text-2xl mb-6">Aanvragen</h2>
            {requests.length === 0 && <p className="text-text-muted py-12 text-center border border-border bg-surface">Nog geen aanvragen.</p>}
            <div className="space-y-3" data-testid="requests-list">
              {requests.map((r) => (
                <div key={r.id} className={`border border-border bg-surface p-5 ${!r.gelezen ? "border-l-4 border-l-primary" : ""}`} data-testid={`request-${r.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="flex items-center gap-3">
                        <span className={`text-xs font-bold uppercase tracking-wide px-2 py-0.5 ${r.type === "proefrit" ? "bg-accent text-primary" : "bg-background text-text-muted"}`}>
                          {r.type === "proefrit" ? "Proefrit" : "Contact"}
                        </span>
                        {!r.gelezen && <span className="text-xs text-primary font-semibold">Nieuw</span>}
                        {r.bevestiging_verstuurd === true && (
                          <span className="text-xs font-semibold px-2 py-0.5 bg-emerald-100 text-emerald-700 inline-flex items-center gap-1" data-testid={`confirm-sent-${r.id}`}>
                            <CheckCircle2 size={12} /> Klant bevestigd
                          </span>
                        )}
                        {r.bevestiging_verstuurd === false && (
                          <span className="text-xs font-semibold px-2 py-0.5 bg-amber-100 text-amber-700" data-testid={`confirm-failed-${r.id}`}>
                            Bevestiging niet verstuurd
                          </span>
                        )}
                      </div>
                      <p className="font-display font-bold text-lg mt-2">{r.naam}</p>
                      {r.auto_titel && <p className="text-sm text-primary font-semibold">Auto: {r.auto_titel}</p>}
                      <div className="flex flex-wrap gap-4 mt-2 text-sm text-text-muted">
                        <a href={`mailto:${r.email}`} className="flex items-center gap-1.5 hover:text-primary"><Mail size={14} /> {r.email}</a>
                        {r.telefoon && <a href={`tel:${r.telefoon}`} className="flex items-center gap-1.5 hover:text-primary"><Phone size={14} /> {r.telefoon}</a>}
                      </div>
                      {r.bericht && <p className="mt-3 text-text-muted text-sm max-w-2xl whitespace-pre-line">{r.bericht}</p>}
                    </div>
                    <div className="flex gap-2">
                      {!r.gelezen && <button onClick={() => markRead(r.id)} className="p-2 text-text-muted hover:text-primary transition-colors" title="Markeer als gelezen"><CheckCircle2 size={18} /></button>}
                      <button onClick={() => delReq(r.id)} data-testid={`delete-request-${r.id}`} className="p-2 text-text-muted hover:text-red-600 transition-colors"><Trash2 size={18} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "populair" && (
          <div data-testid="populair-tab">
            <h2 className="font-display font-bold text-2xl mb-6">Populaire occasions</h2>
            <div className="grid grid-cols-2 gap-px bg-border border border-border mb-10 max-w-md">
              <Stat label="Totaal bekeken" value={stats?.totaal_views ?? 0} />
              <Stat label="Totaal gedeeld" value={stats?.totaal_shares ?? 0} accent />
            </div>
            <div className="grid lg:grid-cols-2 gap-6">
              <PopularList
                title="Meest bekeken"
                icon={Eye}
                items={stats?.populair_views || []}
                metric="views"
                metricLabel="keer bekeken"
              />
              <PopularList
                title="Meest gedeeld"
                icon={Share2}
                items={stats?.populair_shares || []}
                metric="shares"
                metricLabel="keer gedeeld"
              />
            </div>
          </div>
        )}

        {tab === "teksten" && <ContentEditor />}
      </div>

      {editing !== null && (
        <CarForm
          initial={editing.id ? editing : null}
          onCancel={() => setEditing(null)}
          onDone={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

const TabBtn = ({ active, onClick, icon: Icon, label }) => (
  <button onClick={onClick} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${active ? "bg-primary text-white" : "text-text-muted hover:text-primary"}`}>
    <Icon size={16} /> {label}
  </button>
);

const Stat = ({ label, value, accent }) => (
  <div className="bg-surface p-6">
    <p className={`font-display font-black text-4xl ${accent ? "text-primary" : "text-text-main"}`}>{value}</p>
    <p className="text-text-muted text-sm mt-1">{label}</p>
  </div>
);

const PopularList = ({ title, icon: Icon, items, metric, metricLabel }) => (
  <div className="border border-border bg-surface" data-testid={`popular-${metric}`}>
    <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
      <Icon size={18} className="text-primary" />
      <h3 className="font-display font-bold text-lg">{title}</h3>
    </div>
    {items.length === 0 ? (
      <p className="text-text-muted text-sm p-6">Nog geen gegevens — zodra bezoekers auto's bekijken of delen verschijnt hier de ranglijst.</p>
    ) : (
      <ol className="divide-y divide-border">
        {items.map((c, i) => (
          <li key={c.id}>
            <Link to={`/occasions/${c.id}`} className="flex items-center gap-4 px-5 py-3.5 hover:bg-background/60 transition-colors">
              <span className="font-display font-black text-lg text-text-muted w-5 shrink-0">{i + 1}</span>
              <div className="w-14 h-11 bg-[#eef2f0] overflow-hidden shrink-0">
                {c.image && <img src={fileUrl(c.image)} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-text-main truncate">{c.merk} {c.model}</p>
                <p className="text-xs text-text-muted">{euro(c.prijs)}</p>
              </div>
              <span className="font-display font-black text-xl text-primary shrink-0">{c[metric]}</span>
              <span className="text-xs text-text-muted hidden sm:block shrink-0">{metricLabel}</span>
            </Link>
          </li>
        ))}
      </ol>
    )}
  </div>
);
